// React and third-party imports
import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  Dispatch,
  SetStateAction,
} from "react";
import { useQueryState } from "nuqs";

// Shared library imports
import { useInfinitePagination } from "shared/lib/hooks/index";
import { UseInfinitePaginationReturnI } from "shared/lib/hooks/infinite-scrolling/use-infinite-pagination";
import {
  AccountDetailsStatusI,
  AccountDetailsSummaryI,
  AccountDetailsTabI,
  AccountI,
  AccountUserListI,
  SchedulingConfigI,
} from "shared/lib/interfaces/account";

// API imports
import { APII, glencocoClientAPI } from "@/api/glencoco";
import { UseFetchAccountDetailsApiI } from "@/api/routes/account/queries";

// Constants imports
import { ACCOUNTS_DETAILS_TABS } from "@/modules/pipeline/account-details/constants";
import { DIALER_CALL_STATUS } from "@/constants/dialer";
import {
  LEFT_SIDE_OPTIONS,
  RIGHT_SIDE_OPTIONS,
} from "./nav/view-options/constants";

// Hooks imports
import { useSearchQueryParams } from "@/hooks/use-search-query-params";
import { useDialerCallStatus } from "@/hooks/dialer/use-dialer-call-status";

// Interface imports
import {
  AccountExecutiveI,
  AccountHistoryItemI,
  ContactDetailI,
} from "@/lib/types/accounts";
import { CampaignI } from "@/lib/types/campaign";
import { AccountNoteI } from "@/lib/types/account-details";
import { AccountDetailsViewOptionI } from "./nav/view-options/interface";
import { EmailFollowUpConfigByDispositionI } from "./sections/disposition-section/interface";

// Utility imports
import { getLeadStateFlags } from "@/modules/pipeline/account-details/utils";
import { syncOptions } from "./nav/view-options/utils";
import { LocalStorage } from "@/helpers/local-storage";
import { SessionStorage } from "@/helpers/session-storage";
import { GetShouldSendEmailRequestParamsI } from "@/api/routes/email";
import { WSCallCopilotMessageI } from "@/lib/types/events";

export interface AccountDetailsContextI {
  activeTab?: string;
  setActiveTab: (tab: string | null) => void;

  account?: AccountI;
  accountSummary?: AccountDetailsSummaryI;
  campaign?: CampaignI;
  contacts?: Array<ContactDetailI>;
  contactsForContextMenu: Array<ContactDetailI>;
  accountStatus?: AccountDetailsStatusI;
  accountExecutive?: AccountExecutiveI | null;
  accountUserLists?: AccountUserListI[];
  accountDetailsApi?: UseFetchAccountDetailsApiI;
  scheduling?: SchedulingConfigI;

  accountNote?: AccountNoteI;
  setAccountNote?: (note: AccountNoteI) => void;

  dialerToolkitViewOptions?: AccountDetailsViewOptionI[];
  setDialerToolkitViewOptions?: (options: AccountDetailsViewOptionI[]) => void;

  accountHistoryData: UseInfinitePaginationReturnI<AccountHistoryItemI>;

  focusedContact: ContactDetailI | undefined;
  setFocusedContact: (contact?: ContactDetailI) => void;

  isEmailSection?: boolean;
  setIsEmailSection?: (isEmailSection: boolean) => void;

  emailFollowUpData?: EmailFollowUpConfigByDispositionI;
  setEmailFollowUpData?: (props?: EmailFollowUpConfigByDispositionI) => void;

  emailFollowUpRequestParams?: GetShouldSendEmailRequestParamsI;
  setEmailFollowUpRequestParams?: (
    params?: GetShouldSendEmailRequestParamsI
  ) => void;

  callCopilotContent?: WSCallCopilotMessageI[];
  setCallCopilotContent?: Dispatch<
    SetStateAction<WSCallCopilotMessageI[] | undefined>
  >;

  onAccountUpdate: () => void;
}

export interface AccountDetailsProviderI {
  account?: AccountI;
  accountSummary?: AccountDetailsSummaryI;
  campaign?: CampaignI;
  contacts?: Array<ContactDetailI>;
  accountStatus?: AccountDetailsStatusI;
  accountExecutive?: AccountExecutiveI | null;
  accountNote?: AccountNoteI;
  accountUserLists?: AccountUserListI[];
  accountDetailsApi?: UseFetchAccountDetailsApiI;
  scheduling?: SchedulingConfigI;

  accountHistory?: Array<AccountHistoryItemI>;

  onAccountUpdate: () => void;
  tab?: AccountDetailsTabI;
}

const accountHistoryFetcher = (
  campaignId: string,
  accountId: string,
  nextToken: string | undefined
) => glencocoClientAPI().getAccountHistoryV2(campaignId, accountId, nextToken);

const AccountDetailsContext = createContext<AccountDetailsContextI>({
  activeTab: undefined,
  setActiveTab: () => {},
  focusedContact: undefined,
  setFocusedContact: () => {},
  onAccountUpdate: () => {},
  accountHistoryData: {
    isLoading: false,
    isReachedEnd: false,
    data: [],
    reloadData: () => {},
    loadMore: () => {},
  },
  accountDetailsApi: {} as UseFetchAccountDetailsApiI,
  contactsForContextMenu: [],
});

export const AccountDetailsProvider: FC<
  AccountDetailsProviderI & PropsWithChildren
> = ({
  campaign: campaignData,
  account: accountData,
  contacts: contactsData,
  accountUserLists: accountUserListsData,
  accountStatus: accountStatusData,
  accountSummary: accountSummaryData,
  accountNote: accountNoteData,

  onAccountUpdate,
  accountExecutive: accountExecutiveProp,
  accountDetailsApi,
  scheduling,
  children,
}) => {
  const callStatus = useDialerCallStatus();
  const activeTabFromSearchQuery = useSearchQueryParams("tab");
  const [activeTab, setActiveTab] = useQueryState("tab", {
    defaultValue: activeTabFromSearchQuery || ACCOUNTS_DETAILS_TABS.DETAILS,
  });

  useEffect(() => {
    if (
      [DIALER_CALL_STATUS.DURING as string, DIALER_CALL_STATUS.AFTER].includes(
        callStatus
      )
    ) {
      setActiveTab(ACCOUNTS_DETAILS_TABS.DIALER_TOOLKIT);
    }
  }, [callStatus]);

  const [focusedContact, setFocusedContact] = useState<ContactDetailI>();

  const [campaign, setCampaign] = useState(campaignData);
  useEffect(() => setCampaign(campaignData), [campaignData]);

  const [account, setAccount] = useState(accountData);
  useEffect(() => setAccount(accountData), [accountData]);

  const [contacts, setContacts] = useState(contactsData);
  useEffect(() => setContacts(contactsData), [contactsData]);

  const [accountStatus, setAccountStatus] = useState(accountStatusData);
  useEffect(() => setAccountStatus(accountStatusData), [accountStatusData]);

  const [accountSummary, setAccountSummary] = useState(accountSummaryData);
  useEffect(() => setAccountSummary(accountSummaryData), [accountSummaryData]);

  const [accountExecutive, setAccountExecutive] =
    useState(accountExecutiveProp);
  useEffect(
    () => setAccountExecutive(accountExecutiveProp),
    [accountExecutiveProp]
  );

  const [accountUserLists, setAccountUserLists] =
    useState(accountUserListsData);
  useEffect(
    () => setAccountUserLists(accountUserListsData),
    [accountUserListsData]
  );

  const [accountNote, setAccountNote] = useState(accountNoteData);
  useEffect(() => setAccountNote(accountNoteData), [accountNoteData]);

  const [dialerToolkitViewOptions, setDialerToolkitViewOptions] = useState([
    ...syncOptions(LEFT_SIDE_OPTIONS),
    ...syncOptions(RIGHT_SIDE_OPTIONS),
  ]);
  useEffect(() => {
    const LS = new LocalStorage();
    LS.accountDetailsViewOptions = [...dialerToolkitViewOptions];
  }, [dialerToolkitViewOptions]);

  const [isEmailSection, setIsEmailSection] = useState(false);

  /**
   * Use case
   * When call context is cleared, we need to clear the emails local drafts
   */
  useEffect(() => {
    if (!isEmailSection && callStatus === DIALER_CALL_STATUS.BEFORE) {
      const SS = new SessionStorage();
      SS.emailLocalDraftsByCallId = {};
    }
  }, [isEmailSection, callStatus]);

  /**
   * Returned after disposition submission and valid only
   * in email section after that it will be reset
   */
  const [emailFollowUpData, setEmailFollowUpData] =
    useState<EmailFollowUpConfigByDispositionI>();

  const [emailFollowUpRequestParams, setEmailFollowUpRequestParams] =
    useState<GetShouldSendEmailRequestParamsI>();

  const [callCopilotContent, setCallCopilotContent] =
    useState<WSCallCopilotMessageI[]>();

  const accountHistoryData = useInfinitePagination<AccountHistoryItemI, APII>({
    isInitiallyLoading: true,
    apiFactory: glencocoClientAPI,
    collectionKeyInResponse: "activity_logs",
    fetchMore: (nextToken) =>
      accountHistoryFetcher(
        campaign?.id as string,
        account?.id as string,
        nextToken
      ),
    errorMessage:
      "Failed to load account history. Please message us in the chat window on the bottom right so we can assist.",
  });

  // As `contacts` represent a full collection of contacts including ones that
  // are disqualified or marked as "do not call", we need to filter them out for
  // Disqualify, Email, Add to List, Book/Reschedule Meeting and Call buttons.
  const contactsForContextMenu = useMemo(() => {
    if (!contacts) {
      return [];
    }

    return contacts.filter((contact) => {
      const { isLeadDisqualified, isLeadMarkedDNC } =
        getLeadStateFlags(contact);

      return !isLeadDisqualified && !isLeadMarkedDNC;
    });
  }, [contacts]);

  return (
    <AccountDetailsContext.Provider
      value={{
        activeTab,
        setActiveTab,
        campaign,
        account,
        accountSummary,

        accountNote,
        setAccountNote,

        isEmailSection,
        setIsEmailSection,

        emailFollowUpData,
        setEmailFollowUpData,

        emailFollowUpRequestParams,
        setEmailFollowUpRequestParams,

        callCopilotContent,
        setCallCopilotContent,

        dialerToolkitViewOptions,
        setDialerToolkitViewOptions,
        contacts,
        contactsForContextMenu,
        accountStatus,
        accountHistoryData,
        focusedContact,
        setFocusedContact,
        onAccountUpdate,
        accountExecutive,
        accountUserLists,
        accountDetailsApi,
        scheduling,
      }}
    >
      {children}
    </AccountDetailsContext.Provider>
  );
};

export const useAccountDetailsContext = (): AccountDetailsContextI =>
  useContext(AccountDetailsContext);
